/*
 * <instafeed-feed>: Instagram feed from the Instafeed storefront API (GET /feed/v6 or /feed/v5) in a Swiper
 * slider, with a full-screen popup slider per post.
 *
 * Speed: the last response is kept in localStorage (1 hour) and rendered right away on the next visit, then
 * refreshed in the background. Popup media only loads for the open post and its neighbours.
 */
(() => {
  if (window.customElements.get('instafeed-feed')) return;

  const CACHE_TTL = 60 * 60 * 1000;

  const ICONS = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8.5 5.8v12.4a.8.8 0 0 0 1.2.7l10-6.2a.8.8 0 0 0 0-1.4l-10-6.2a.8.8 0 0 0-1.2.7Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="17.4" cy="6.6" r="1.2" fill="currentColor"/></svg>',
    muted: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" vector-effect="non-scaling-stroke" d="m15.5 9.5 5 5m0-5-5 5"/></svg>',
    sound: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" vector-effect="non-scaling-stroke" d="M15.5 9a4.2 4.2 0 0 1 0 6m2.5-8.5a7.6 7.6 0 0 1 0 11"/></svg>',
  };

  const el = (tag, className, attributes = {}) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value !== undefined && value !== null) node.setAttribute(name, value);
    });
    return node;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class InstafeedFeed extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      this.section = this.closest('.shopify-section');
      this.swiperEl = this.querySelector('[data-instafeed-swiper]');
      this.wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      this.prevButton = this.querySelector('[data-instafeed-prev]');
      this.nextButton = this.querySelector('[data-instafeed-next]');
      this.progress = this.querySelector('[data-instafeed-progress]');
      this.modal = this.querySelector('[data-instafeed-modal]');
      this.posts = [];
      this.meta = {};

      if (!this.dataset.apiKey) return;
      this.load();
    }

    disconnectedCallback() {
      this.swiper?.destroy(true, true);
      this.modalSwiper?.destroy(true, true);
    }

    get cacheKey() {
      const { account, feedId, handle, limit, version } = this.dataset;
      return `instafeed:${version}:${account}:${feedId}:${handle || ''}:${limit}`;
    }

    readCache() {
      try {
        const cached = JSON.parse(localStorage.getItem(this.cacheKey));
        if (cached && Date.now() - cached.time < CACHE_TTL) return cached.feed;
      } catch (error) {
        // Storage unavailable or corrupt: fetch instead.
      }
      return null;
    }

    writeCache(feed) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({ time: Date.now(), feed }));
      } catch (error) {
        // Storage full or blocked: nothing to do.
      }
    }

    async fetchFeed() {
      const { account, feedId, handle, limit, version, apiKey } = this.dataset;
      const url = new URL(`https://instafeed.nfcube.com/feed/${version || 'v6'}`);
      url.searchParams.set('limit', limit || '12');
      url.searchParams.set('account', account);
      url.searchParams.set('fu', '0');
      url.searchParams.set('fid', feedId || '0');
      if (handle) url.searchParams.set('handle', handle);

      // 202: the app is still building its cache, try again a few times.
      for (let attempt = 0; attempt < 4; attempt++) {
        const response = await fetch(url, { headers: { 'X-API-Key': apiKey } });
        if (response.status === 202) {
          await wait(2500);
          continue;
        }
        if (response.status === 204) return { meta: {}, data: [] };
        if (!response.ok) throw new Error(`Instafeed ${response.status}`);
        const json = await response.json();
        return { meta: json.meta || {}, data: (json.data || []).filter((post) => post?.images?.standard_resolution?.url) };
      }
      throw new Error('Instafeed feed not ready');
    }

    async load() {
      const cached = this.readCache();
      if (cached) this.render(cached);

      try {
        const feed = await this.fetchFeed();
        this.writeCache(feed);
        const changed = !cached || JSON.stringify(cached.data.map((post) => post.id)) !== JSON.stringify(feed.data.map((post) => post.id));
        if (changed) this.render(feed);
      } catch (error) {
        if (!cached) this.renderEmpty();
      }
    }

    renderEmpty() {
      if (window.Shopify?.designMode) {
        this.classList.add('instafeed--empty');
        return;
      }
      if (this.section) this.section.hidden = true;
    }

    render(feed) {
      if (!feed.data.length) return this.renderEmpty();

      this.posts = feed.data;
      this.meta = feed.meta || {};
      this.wrapper.replaceChildren(...this.posts.map((post, index) => this.buildSlide(post, index)));
      this.classList.add('instafeed--ready');

      if (this.modalSwiper) {
        this.modalSwiper.destroy(true, true);
        this.modalSwiper = null;
      }

      if (this.swiper) {
        this.swiper.update();
        this.swiper.slideTo(0, 0);
      } else {
        this.initSwiper();
      }
    }

    buildSlide(post, index) {
      const slide = el('div', 'swiper-slide instafeed__slide');
      const isVideo = post.type === 'video';
      const caption = post.caption?.text?.trim() || '';
      const label = caption ? caption.slice(0, 100) : post.user?.username || 'Instagram';

      const trigger = this.dataset.popup === 'true'
        ? el('button', 'instafeed__post', { type: 'button', 'data-index': index, 'aria-label': label })
        : el('a', 'instafeed__post', { href: post.link, target: '_blank', rel: 'noopener', 'aria-label': label });

      /* Instagram only allows the full-size file on other sites (its resized copies are blocked cross-site),
         so only the posts on the first screen load right away; the rest load as they come near. */
      const firstScreen = index < Math.ceil(this.cssNumber('--instafeed-spv', 3.5));
      const image = el('img', 'instafeed__image', {
        src: post.images.standard_resolution.url,
        alt: post.alt_text || '',
        loading: firstScreen ? 'eager' : 'lazy',
        fetchpriority: firstScreen ? 'high' : 'low',
        decoding: 'async',
        draggable: 'false',
      });

      const overlay = el('span', `instafeed__overlay instafeed__overlay--${isVideo ? 'video' : 'post'}`, { 'aria-hidden': 'true' });
      const icon = el('span', `instafeed__icon instafeed__icon--${isVideo ? 'play' : 'instagram'}`);
      icon.innerHTML = isVideo ? ICONS.play : ICONS.instagram;
      overlay.append(icon);

      trigger.append(image, overlay);
      slide.append(trigger);
      return slide;
    }

    cssNumber(name, fallback) {
      const value = parseFloat(getComputedStyle(this).getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    }

    // Same fill as the theme's scroll progress bar: visible share + scrolled share of the rest.
    updateProgress(swiper) {
      if (!this.progress) return;
      const visible = swiper.virtualSize > 0 ? Math.min(1, swiper.width / swiper.virtualSize) : 1;
      const scrolled = Math.min(1, Math.max(0, swiper.progress || 0));
      this.progress.style.setProperty('--progress-bar-progress', visible + scrolled * (1 - visible));
    }

    initSwiper() {
      if (!window.Swiper) {
        window.addEventListener('load', () => this.initSwiper(), { once: true });
        return;
      }

      const spacing = () => this.cssNumber('--instafeed-gap', 12);
      const padding = () => this.cssNumber('--instafeed-pad', 32);

      this.swiper = new window.Swiper(this.swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: spacing(),
        slidesOffsetAfter: padding(),
        watchOverflow: true,
        grabCursor: true,
        speed: 450,
        mousewheel: { forceToAxis: true },
        navigation: this.prevButton ? { prevEl: this.prevButton, nextEl: this.nextButton, disabledClass: 'is-disabled' } : false,
        on: {
          // Gap and edge padding follow the CSS custom properties of the current breakpoint.
          beforeResize: (swiper) => {
            swiper.params.spaceBetween = spacing();
            swiper.params.slidesOffsetAfter = padding();
          },
          init: (swiper) => this.updateProgress(swiper),
          progress: (swiper) => this.updateProgress(swiper),
          resize: (swiper) => this.updateProgress(swiper),
          update: (swiper) => this.updateProgress(swiper),
        },
      });

      this.wrapper.addEventListener('click', (event) => {
        const trigger = event.target.closest('button.instafeed__post');
        if (trigger) this.openModal(Number(trigger.dataset.index));
      });
    }

    /* ---------- Popup ---------- */

    formatDate(seconds) {
      if (!seconds) return '';
      const date = new Date(Number(seconds) * 1000);
      const options = { month: 'long', day: 'numeric' };
      if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
      try {
        return new Intl.DateTimeFormat(document.documentElement.lang || undefined, options).format(date);
      } catch (error) {
        return date.toLocaleDateString();
      }
    }

    buildModalSlide(post) {
      const slide = el('div', 'swiper-slide instafeed-modal__slide');
      const card = el('article', 'instafeed-modal__card');
      const media = el('div', 'instafeed-modal__media');

      if (post.type === 'video' && post.videos?.standard_resolution?.url) {
        const video = el('video', 'instafeed-modal__video', {
          'data-src': post.videos.standard_resolution.url,
          'data-poster': post.images.standard_resolution.url,
          playsinline: '',
          loop: '',
          preload: 'none',
        });
        video.muted = true;
        const mute = el('button', 'instafeed-modal__mute', { type: 'button', 'aria-label': this.dataset.labelUnmute || 'Unmute' });
        mute.innerHTML = ICONS.muted;
        media.append(video, mute);
      } else {
        media.append(el('img', 'instafeed-modal__image', { 'data-src': post.images.standard_resolution.url, alt: post.alt_text || '', decoding: 'async' }));
      }

      const info = el('div', 'instafeed-modal__info');
      const header = el('header', 'instafeed-modal__header');
      // Avatar and username are separate links, so only they open the profile (not the space between them).
      const username = post.user?.username || this.meta.username || '';
      const profileUrl = `https://www.instagram.com/${username}/`;
      const profile = el('div', 'instafeed-modal__profile');
      if (this.meta.profile_picture) {
        const avatarLink = el('a', 'instafeed-modal__avatar-link', { href: profileUrl, target: '_blank', rel: 'noopener', 'aria-label': username });
        avatarLink.append(el('img', 'instafeed-modal__avatar', { src: this.meta.profile_picture, alt: '', loading: 'lazy', width: '44', height: '44' }));
        profile.append(avatarLink);
      }
      const name = el('a', 'instafeed-modal__username', { href: profileUrl, target: '_blank', rel: 'noopener' });
      name.textContent = username;
      profile.append(name);
      header.append(profile);

      const caption = el('div', 'instafeed-modal__caption', { dir: 'auto' });
      caption.textContent = post.caption?.text?.trim() || '';

      const footer = el('footer', 'instafeed-modal__footer');
      const date = el('time', 'instafeed-modal__date');
      date.textContent = this.formatDate(post.created_time);
      const link = el('a', 'instafeed-modal__link', { href: post.link, target: '_blank', rel: 'noopener' });
      link.textContent = this.dataset.labelView || 'View on Instagram';
      footer.append(date, link);

      info.append(header, caption, footer);
      card.append(media, info);
      slide.append(card);
      return slide;
    }

    ensureModal() {
      if (this.modalSwiper || !this.modal) return;

      const modalWrapper = this.modal.querySelector('.swiper-wrapper');
      modalWrapper.replaceChildren(...this.posts.map((post) => this.buildModalSlide(post)));

      this.modalSwiper = new window.Swiper(this.modal.querySelector('[data-instafeed-modal-swiper]'), {
        slidesPerView: 1,
        spaceBetween: 40,
        speed: 400,
        keyboard: { enabled: true },
        navigation: {
          prevEl: this.modal.querySelector('[data-instafeed-modal-prev]'),
          nextEl: this.modal.querySelector('[data-instafeed-modal-next]'),
          disabledClass: 'is-disabled',
        },
        on: {
          slideChange: () => this.syncModalMedia(),
        },
      });

      if (this._modalEvents) return;
      this._modalEvents = true;

      this.modal.querySelector('[data-instafeed-modal-close]').addEventListener('click', () => this.modal.close());
      // A click on the dark backdrop (outside the card) closes the popup.
      this.modal.addEventListener('click', (event) => {
        if (event.target.closest('.instafeed-modal__card, button')) return;
        this.modal.close();
      });
      this.modal.addEventListener('close', () => {
        this.modal.querySelectorAll('video').forEach((video) => video.pause());
        document.documentElement.classList.remove('instafeed-modal-open');
      });
      this.modal.addEventListener('click', (event) => {
        const mute = event.target.closest('.instafeed-modal__mute');
        if (!mute) return;
        const video = mute.parentElement.querySelector('video');
        this.soundOn = video.muted;
        this.modal.querySelectorAll('video').forEach((item) => (item.muted = !this.soundOn));
        this.modal.querySelectorAll('.instafeed-modal__mute').forEach((button) => {
          button.innerHTML = this.soundOn ? ICONS.sound : ICONS.muted;
          button.setAttribute('aria-label', this.soundOn ? this.dataset.labelMute || 'Mute' : this.dataset.labelUnmute || 'Unmute');
        });
      });
    }

    // Load the open post and its neighbours; play only the open video.
    syncModalMedia() {
      const active = this.modalSwiper.activeIndex;
      this.modalSwiper.slides.forEach((slide, index) => {
        const near = Math.abs(index - active) <= 1;
        if (near) {
          slide.querySelectorAll('[data-poster]:not([poster])').forEach((video) => video.setAttribute('poster', video.dataset.poster));
          slide.querySelectorAll('[data-src]:not([src])').forEach((media) => media.setAttribute('src', media.dataset.src));
        }
        const video = slide.querySelector('video');
        if (!video) return;
        if (index === active && this.modal.open) {
          video.muted = !this.soundOn;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    openModal(index) {
      if (!this.modal || !window.Swiper) return;
      this.ensureModal();
      this.modalSwiper.slideTo(index, 0);
      this.modal.showModal();
      document.documentElement.classList.add('instafeed-modal-open');
      this.modalSwiper.update();
      this.syncModalMedia();
    }
  }

  window.customElements.define('instafeed-feed', InstafeedFeed);
})();
